"use client";

import CustomPreset24 from "@ui/icons/24x24/custom-preset-24.svg";
import NewPreset24 from "@ui/icons/24x24/new-preset-24.svg";
import { cn } from "@ui/lib/utils";
import { useTranslations } from "next-intl";

import { ActionTypes, addPreset, openPresetEditor } from "../state/actions";
import { useSoundboard } from "../state/reducer";
import { type Preset, PresetCategory } from "../types";
import { SoundBoardButton } from "./SoundBoardButton";

export const NewPresetButton = ({ flat }: { flat?: boolean }) => {
  const { dispatch } = useSoundboard();
  const t = useTranslations("ClientKeys.soundboard");
  return (
    <SoundBoardButton className={cn("create-button", flat && "aspect-2x1")} onClick={() => dispatch(addPreset())}>
      {!flat && <NewPreset24 />}
      <p className='font-soundboard f-ui-04 w-full truncate text-inverse'>{t("new_preset_button.label")}</p>
    </SoundBoardButton>
  );
};

const PresetButton = ({ preset }: { preset: Preset }) => {
  const { dispatch, state } = useSoundboard();
  const isActive = state.activePresetId === preset.id;
  const handleClick = () => {
    if (state.isInEditMode) {
      dispatch(openPresetEditor(preset.id));
    } else {
      dispatch({ type: ActionTypes.ACTIVATE_PRESET, payload: preset.id });
    }
  };

  return (
    <SoundBoardButton className={cn("control-button", isActive && "selected")} onClick={handleClick}>
      {preset.icon ? <img alt='' className='h-24 w-24 text-inverse' src={preset.icon} /> : <CustomPreset24 />}
      <p className='font-soundboard f-ui-04 w-full truncate text-inverse'>{preset.name}</p>
    </SoundBoardButton>
  );
};

const LoadingPresetButton = () => {
  return <SoundBoardButton className='control-button' disabled />;
};

export const PresetsRow = () => {
  const { state } = useSoundboard();
  const visiblePresets =
    state.activePresetCategory === PresetCategory.OWN
      ? Object.values(state.presets).filter(preset => preset.isCreatedByMe)
      : Object.values(state.presets).filter(preset => !preset.isCreatedByMe);

  const isLoading =
    (state.activePresetCategory === PresetCategory.OWN && state.isLoadingSavedPresets) ||
    (state.activePresetCategory === PresetCategory.DEFAULTS && visiblePresets.length === 0);

  return (
    <div className='grid grid-cols-4 gap-4'>
      {isLoading && Array.from({ length: 4 }).map((_, index) => <LoadingPresetButton key={index} />)}
      {visiblePresets.map(preset => (
        <PresetButton key={preset.id} preset={preset} />
      ))}
    </div>
  );
};
